/* ═══════════════════════════════════════════════════════════════
   uiScale (renderer controller) — the browser half of DESIGN.md §3.6.
   The curve itself (window width → dial, dial × user → effective) lives
   in `src/shared/uiScale.ts`; this file is the only thing that reads
   `window.innerWidth`, localStorage, or writes to the DOM for it.

   Root font-size = 16px × effective. `mountUiScale()` is called once
   from the app shell; it sets the root font-size + `--ui-dial`/
   `--ui-user`/`--ui-scale` custom properties, and (desktop only) wires
   Ctrl+=/Ctrl+-/Ctrl+0 to the user factor. No CSS `zoom`, no page zoom —
   this is a real font-size change so drag/measure code keeps working.

   Toast: the app has no importable toast singleton — `toast()` is a
   closure created inside `app.tsx` and only reaches components as a
   prop through React context. This module can't call it. Instead every
   user-driven change fires `subscribeUiScale` (for a caller with real
   React context to show its own toast) and a `sf-ui-scale` window
   CustomEvent (for surfaces like T7 to react without a subscription).
   ═══════════════════════════════════════════════════════════════ */

import {
  ROOT_PX,
  autoScale,
  clampUser,
  effectiveScale,
  stepUser,
  type UiScaleValues,
} from '../../shared/uiScale';
import { isWeb } from './platform';

export type { UiScaleValues };

const STORAGE_KEY = 'sf-ui-user-scale';
const EVENT_NAME = 'sf-ui-scale';
const RESIZE_DEBOUNCE_MS = 100;

let state: UiScaleValues = { dial: 1, user: 1, effective: 1 };
const listeners = new Set<(v: UiScaleValues) => void>();

let mounted = false;
let currentUnmount: (() => void) | null = null;

function readStoredUser(): number {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw == null) return 1;
    const n = parseFloat(raw);
    return Number.isFinite(n) ? clampUser(n) : 1;
  } catch {
    return 1;
  }
}

function writeStoredUser(user: number): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, String(user));
  } catch {
    // storage unavailable (private mode, etc.) — in-memory value still applies
  }
}

function applyToDom(): void {
  const root = document.documentElement;
  root.style.fontSize = ROOT_PX * state.effective + 'px';
  root.style.setProperty('--ui-dial', String(state.dial));
  root.style.setProperty('--ui-user', String(state.user));
  root.style.setProperty('--ui-scale', String(state.effective));
}

function notify(): void {
  const snapshot = getUiScale();
  listeners.forEach((cb) => cb(snapshot));
  window.dispatchEvent(new CustomEvent<UiScaleValues>(EVENT_NAME, { detail: snapshot }));
}

function recompute(): void {
  const dial = autoScale(window.innerWidth);
  state = { dial, user: state.user, effective: effectiveScale(dial, state.user) };
}

function setUser(user: number): void {
  state = { ...state, user, effective: effectiveScale(state.dial, user) };
  writeStoredUser(user);
  applyToDom();
  notify();
}

/** Current dial/user/effective values. Safe to call before `mountUiScale` —
 *  returns the pre-mount default (1/1/1). */
export function getUiScale(): UiScaleValues {
  return { ...state };
}

/** Set the user factor directly (already-clamped values pass through
 *  unchanged); fires the change notification. */
export function setUserScale(n: number): void {
  setUser(clampUser(n));
}

/** One Ctrl+=/Ctrl+- step. */
export function stepUserScale(dir: 1 | -1): void {
  setUser(stepUser(state.user, dir));
}

/** Ctrl+0 — back to 100% user factor (dial is untouched, it's automatic). */
export function resetUserScale(): void {
  setUser(1);
}

/** Subscribe to every change (resize-driven dial change or a user step) —
 *  for a Settings row or a component with real React context to show its
 *  own toast. Returns an unsubscribe function. */
export function subscribeUiScale(cb: (v: UiScaleValues) => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

/** Mount the controller once from the app shell. Idempotent — a second
 *  call is a no-op that returns the SAME unmount function; only the
 *  first caller's `mountUiScale()` actually tears anything down. */
export function mountUiScale(): () => void {
  if (mounted && currentUnmount) return currentUnmount;
  mounted = true;

  state = { ...state, user: readStoredUser() };
  recompute();
  applyToDom();

  let resizeTimer: ReturnType<typeof setTimeout> | undefined;
  const onResize = () => {
    if (resizeTimer) clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => {
      const prevEffective = state.effective;
      recompute();
      applyToDom();
      if (state.effective !== prevEffective) notify();
    }, RESIZE_DEBOUNCE_MS);
  };
  window.addEventListener('resize', onResize);

  // Desktop-only keyboard shortcuts — the web app leaves the browser's own
  // Ctrl+=/Ctrl+- alone. Capture phase so this always sees the combo first;
  // preventDefault fires ONLY for the exact combos below.
  let onKeyDown: ((e: KeyboardEvent) => void) | undefined;
  if (!isWeb) {
    onKeyDown = (e: KeyboardEvent) => {
      if (!e.ctrlKey || e.altKey || e.metaKey) return;
      if (e.code === 'Equal' || e.code === 'NumpadAdd') {
        e.preventDefault();
        stepUserScale(1);
      } else if (e.code === 'Minus' || e.code === 'NumpadSubtract') {
        e.preventDefault();
        stepUserScale(-1);
      } else if ((e.code === 'Digit0' || e.code === 'Numpad0') && !e.shiftKey) {
        e.preventDefault();
        resetUserScale();
      }
    };
    window.addEventListener('keydown', onKeyDown, true);
  }

  currentUnmount = () => {
    window.removeEventListener('resize', onResize);
    if (resizeTimer) clearTimeout(resizeTimer);
    if (onKeyDown) window.removeEventListener('keydown', onKeyDown, true);
    mounted = false;
    currentUnmount = null;
  };
  return currentUnmount;
}
